<template>
    <div class="pt-3" id="users-table">
        <v-row class="page-container mb-3 px-0 hidden-sm-and-up">
            <h4 style="color: #363e59">{{ $t('Управление пользователями') }}</h4>
        </v-row>

        <v-row class="page-container mb-3 px-0" no-gutters>
            <v-col cols="12" sm="4" class="px-0">
                <AddEditUser :isNew="true" />
            </v-col>

            <v-col cols="12" sm="6"></v-col>

            <v-col cols="12" sm="2" class="px-0 d-flex justify-end">
                <UsersTableFilter />
            </v-col>
        </v-row>

        <v-data-table
            :loading="userListLoading"
            id="dataTableUsers"
            :class="{ 'elevation-2': !$vuetify.breakpoint.xsOnly }"
            :headers="headers"
            :items="itemsTranslated"
            :options.sync="formListPagination"
            :server-items-length="userListLength"
            :footer-props="{ 'items-per-page-options': $vuetify.breakpoint.xsOnly ? [1] : [10, 15, 20, 25] }"
            ref="table"
        >
            <template v-slot:item.email="{ item }">
                <table-cell-tooltip :header="headers[0]" :item="item">{{ item.email }}</table-cell-tooltip>
            </template>
            <template v-slot:item.name="{ item }">
                <table-cell-tooltip :header="headers[1]" :item="item.user_data.name">{{ item.user_data.name }}</table-cell-tooltip>
            </template>
            <template v-slot:item.isas_serv_org_name="{ item }">
                <table-cell-tooltip :header="headers.find(x => x.value === 'isas_serv_org_name')" :item="item.isas_serv_org_name.isas_serv_org_name">{{
                    item.isas_serv_org_name.isas_serv_org_name
                }}</table-cell-tooltip>
            </template>
            <template v-slot:item.isas_ksk_name="{ item }">
                <table-cell-tooltip :header="headers.find(x => x.value === 'isas_ksk_name')" :item="item.user_data.isas_ksk_name">{{
                    item.user_data.isas_ksk_name
                }}</table-cell-tooltip>
            </template>
            <template v-slot:item.contract_number="{ item }">
                <table-cell-tooltip :header="headers.find(x => x.value === 'contract_number')" :item="String(item.user_data.contract_number)"
                    >{{ item.user_data.contract_number }} {{ item.user_data.contractor_name }}</table-cell-tooltip
                >
            </template>
            <template v-slot:item.role_name="{ item }">
                <table-cell-tooltip :header="headers.find(x => x.value === 'role_name')" :item="item.role_name ? item.role_name.role_name : ''">{{
                    item.role_name ? item.role_name.role_name : ''
                }}</table-cell-tooltip>
            </template>
            <template v-slot:item.active="{ item }">
                <v-container style="width: 100px">
                    <v-row no-gutters>
                        <v-col>
                            <AddEditUser :isNew="false" :details="item" hidden />
                        </v-col>
                        <v-col>
                            <UpdateActiveUserDialog small class="mr-2" :details="item" v-if="item.canDeactivate" hidden />
                        </v-col>
                    </v-row>
                </v-container>
            </template>
        </v-data-table>
        <table-arrows :table="$refs.table"></table-arrows>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import translate from '@/utils/translate.util';
import TableCellTooltip from '@/components/ui/TableCellTooltip';
import TableArrows from '@/components/ui/TableArrows';
import UpdateActiveUserDialog from '@/components/administration/UpdateActiveUserDialog.vue';
import AddEditUser from '@/components/administration/AddEditUser.vue';
import UsersTableFilter from '@/components/administration/UsersTableFilter.vue';

import { isEqual } from 'lodash';

export default {
    components: {
        UpdateActiveUserDialog,
        AddEditUser,
        UsersTableFilter,
        TableCellTooltip,
        TableArrows
    },
    computed: {
        itemsTranslated() {
            // первый параметр - данные из БД, которые нужно перевести
            // второй параметр: full - переводить всё, где встречается постфиксы ___kz или ____ru, part - переводить только label___kz или label___ru
            return translate(this.userList, 'full', this.$i18n.locale);
        },

        ...mapState('users', ['userList', 'userListLoading', 'userListLength', 'currentUser', 'filter']),
        ...mapState('permissions', ['userPermissions']),
        formListPagination: {
            get() {
                return this.$store.state.users.formListPagination;
            },
            set(val) {
                this.$store.commit('users/setFormListPagination', val);
            }
        },
        headers: function() {
            const conditionalColumns = [];
            if (this.userPermissions.some(x => x === 'system_ahs')) {
                conditionalColumns.push({
                    text: this.$t('КСК'),
                    sortable: false,
                    value: 'isas_ksk_name'
                });
                conditionalColumns.push({
                    text: this.$t('Договор'),
                    sortable: false,
                    value: 'contract_number'
                });
            } else if (this.userPermissions.some(x => x === 'system_aho')) {
                conditionalColumns.push({
                    text: this.$t('Сервисная организация'),
                    sortable: false,
                    value: 'isas_serv_org_name'
                });
            }
            return [
                {
                    text: this.$t('E-mail'),
                    value: 'email'
                },
                {
                    text: this.$t('Ф.И.О.'),
                    value: 'name'
                },
                ...conditionalColumns,
                {
                    text: this.$t('Роль'),
                    value: 'role_name'
                },
                {
                    text: this.$t('Заблокирован'),
                    value: 'active',
                    width: '1%'
                }
            ];
        }
    },
    methods: {
        ...mapActions('users', ['getUserList', 'updateIsActive', 'EditUser'])
    },
    async mounted() {
        this.$watch(
            '$vuetify.breakpoint.xsOnly',
            (newVal, oldVal) => {
                if (newVal === oldVal) {
                    return;
                }
                if (newVal) {
                    this.$store.state.users.formListPagination.itemsPerPage = 1;
                }
                if (oldVal) {
                    this.$store.state.users.formListPagination.itemsPerPage = 10;
                }
                this.$store.state.users.formListPagination.page = 1;
            },
            { immediate: true }
        );
        this.$store.commit('users/setFilter', null);
        await this.getUserList();
    },
    watch: {
        formListPagination(newVal, oldVal) {
            // пропускает первичный триггер при инициализации таблицы
            if (isEqual(newVal, oldVal)) return;

            this.getUserList();
        }
    }
};
</script>

<style>
#users-table .page-container {
    max-width: 100% !important;
}
</style>
